/**
 * PeoplePage — the standalone "/people" team page. Reachable only from the "People"
 * link in the landing nav + footer. Like LandingPage / ScheduleDemoPage it renders
 * standalone (no MUI theme wrapper), so it self-imports the self-hosted fonts and the
 * landing-scoped RetroUI stylesheet (`retroui.css`) — the latter pulls in Tailwind's
 * layers (Preflight OFF) + RetroUI :root tokens so every utility class resolves on this
 * route even when a visitor lands here directly.
 *
 * Layout: sticky LandingNav → a brutalist card grid of team members → LandingFooter.
 * Each card shows a framed portrait, the person's name, optional role, and contact
 * links. Mounts inside `.retroui-root`; Preflight is OFF, so every border
 * is written with `border-solid`. ZERO @mui/*.
 *
 * PORTRAITS: square photos under `public/team/<slug>.png`. The asset dir is
 * deliberately NOT named `people` so it can't collide with the /people route (a
 * `public/people/` dir makes static hosts 301 /people -> /people/). To change a
 * photo, replace the file at the same path and keep the `img` field in sync.
 */

// Self-hosted fonts: Archivo Black headings, Space Grotesk body, Space Mono mono.
import '@fontsource/archivo-black/400.css';
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/space-mono/400.css';
import '@fontsource/space-mono/700.css';

// Landing-scoped RetroUI stylesheet: Tailwind layers (Preflight OFF) + RetroUI tokens.
import './retroui.css';

import * as React from 'react';
import { Globe, Linkedin, Mail } from 'lucide-react';
import { LandingNav, LandingFooter } from './sections';

interface Person {
  name: string;
  /** Square portrait under public/. Swap the file at this path to change the photo. */
  img: string;
  role?: string;
  /** Area of ownership, shown in smaller text under the role. */
  focus?: string;
  email?: string;
  linkedin?: string;
  website?: string;
}

// Display order is shared by the desktop rows and the smaller-screen layouts.
const PEOPLE: ReadonlyArray<Person> = [
  {
    name: 'Michael Snyder',
    role: 'Scientific Advisor',
    img: '/team/michael-snyder.png',
    email: 'mpsnyder@stanford.edu',
    website: 'https://med.stanford.edu/snyderlab/about.html',
  },
  {
    name: 'Tam Nguyen',
    role: 'Founding Engineer',
    focus: 'Platform & Infrastructure',
    img: '/team/tam-nguyen.png',
    email: 'tamminhnguyen@usf.edu',
    linkedin: 'https://www.linkedin.com/in/ngtam/',
  },
  {
    name: 'Chan Park',
    role: 'Founding Engineer',
    focus: 'Product & Frontend',
    img: '/team/chan-park.png',
    email: 'chan5537@stanford.edu',
    linkedin: 'https://www.linkedin.com/in/chan-park-5009b4207/',
  },
  {
    name: 'Shadab Tanjeed',
    role: 'Past Intern',
    img: '/team/shadab-tanjeed.png',
    email: 'shadabtanjeed@iut-dhaka.edu',
    linkedin: 'https://www.linkedin.com/in/shadabtanjeed/',
  },
  {
    name: 'Viktor Minchev',
    role: 'Past Intern',
    img: '/team/viktor-minchev.png',
    email: 'v_minchev@ucsb.edu',
    linkedin: 'https://www.linkedin.com/in/viktor-minchev/',
    website: 'https://minchev.dev/',
  },
  {
    name: 'Alexander Johansen',
    img: '/team/alexander-johansen.png',
    email: 'arjo@stanford.edu',
    linkedin: 'https://www.linkedin.com/in/alexrosejo/',
    website: 'https://alrojo.github.io/',
  },
];

/** One brutalist person card: framed portrait, name, optional title, and whichever
 *  of email / LinkedIn / website were provided (each with its own icon). */
const PersonCard: React.FC<{ person: Person }> = ({ person }) => (
  <li
    className="border-2 border-solid border-black bg-card shadow-[6px_6px_0_0_#000] transition-shadow hover:shadow-none"
  >
    {/* Square portrait, framed by a bottom rule so it reads as part of the card. */}
    <img
      src={person.img}
      alt={`${person.name} portrait`}
      width={400}
      height={400}
      className="aspect-square w-full border-b-2 border-solid border-black object-cover"
    />

    <div className="p-5">
      <h3 className="font-head text-xl uppercase leading-snug tracking-tight text-foreground">
        {person.name}
      </h3>
      {person.role && (
        <p className="mt-1 font-mono text-xs font-bold uppercase tracking-[0.15em] text-secondary">
          {person.role}
        </p>
      )}
      {person.focus && (
        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
          {person.focus}
        </p>
      )}

      {/* Contact links — only the ones provided for this person. */}
      {(person.email || person.linkedin || person.website) && (
        <div className="mt-4 flex flex-col gap-3">
          {person.email && (
            <a
              href={`mailto:${person.email}`}
              aria-label={`Email ${person.name}`}
              className="inline-flex items-center gap-2 font-sans text-sm text-foreground no-underline transition-colors hover:text-primary"
            >
              <Mail size={18} aria-hidden />
              <span className="break-all">{person.email}</span>
            </a>
          )}
          {person.linkedin && (
            <a
              href={person.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${person.name} on LinkedIn`}
              className="inline-flex items-center gap-2 font-sans text-sm text-foreground no-underline transition-colors hover:text-primary"
            >
              <Linkedin size={18} aria-hidden />
              <span>LinkedIn</span>
            </a>
          )}
          {person.website && (
            <a
              href={person.website}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${person.name} website`}
              className="inline-flex items-center gap-2 font-sans text-sm text-foreground no-underline transition-colors hover:text-primary"
            >
              <Globe size={18} aria-hidden />
              <span>Website</span>
            </a>
          )}
        </div>
      )}
    </div>
  </li>
);

const PeoplePage: React.FC = () => {
  // Match RetroLanding: disable page-level overscroll bounce while mounted; restore on
  // unmount so other routes are unaffected.
  React.useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.overscrollBehavior;
    html.style.overscrollBehavior = 'none';
    return () => {
      html.style.overscrollBehavior = prev;
    };
  }, []);

  // The SPA ships a single static <title>; give this standalone route its own so the
  // browser tab + shares read "People — StudySync". Restored on unmount.
  React.useEffect(() => {
    const prev = document.title;
    document.title = 'People — StudySync';
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <div className="retroui-root min-h-screen overflow-x-clip bg-background font-sans text-foreground antialiased">
      <LandingNav />

      <main className="mx-auto max-w-[1440px] px-4 py-14 md:px-8 md:py-20">
        {/* Header — eyebrow badge + big black uppercase heading, matching the landing. */}
        <div className="text-center">
          <span className="inline-block border-2 border-solid border-black bg-secondary px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white">
            People
          </span>
          <h1 className="mt-6 font-head text-4xl uppercase leading-[0.95] tracking-tight text-foreground sm:text-5xl">
            Meet the <span className="text-primary">team</span>
          </h1>
          <p className="mx-auto mt-6 max-w-xl font-sans text-lg text-muted-foreground">
            The people building StudySync.
          </p>
        </div>

        <ul className="mx-auto mt-14 grid max-w-[672px] list-none grid-cols-1 gap-8 p-0 sm:grid-cols-2 lg:max-w-[1024px] lg:grid-cols-3">
          {PEOPLE.map((person) => (
            <PersonCard key={person.name} person={person} />
          ))}
        </ul>
      </main>

      <LandingFooter />
    </div>
  );
};

export default PeoplePage;
